import { DraftsAndSentItem, DraftsAndSentItemProps } from "@tellescope/ui";
import type { InboxDraftItem } from "../../../hooks";
import { useTellescopeQueries, type TellescopeModelFromName } from "@tellescope/tanstack-query";
import { Pencil, Trash2 } from "lucide-react";
import { useInboxMessageDeleteMutations, useThreadNavigation } from "../../../../../hooks";
import { useTranslation } from "react-i18next";

export interface InboxThreadListDraftItemProps
  extends Pick<
    DraftsAndSentItemProps<TellescopeModelFromName<"endusers">>,
    "title" | "timestamp" | "preview" | "channel"
  > {
  draft: InboxDraftItem;
  enduserIds: string[];
  chatroomsMap: Record<string, TellescopeModelFromName<"chat_rooms">>;
  className?: string;
}

export const InboxThreadListDraftItem = ({
  draft,
  enduserIds,
  chatroomsMap,
  ...rest
}: InboxThreadListDraftItemProps) => {
  const { t } = useTranslation("inbox");
  const { openThread } = useThreadNavigation();
  const { deleteMessage } = useInboxMessageDeleteMutations();

  const chatroom = draft.channel === "Chat" ? chatroomsMap[draft.roomId] : undefined;

  const endusersQuery = useTellescopeQueries({
    model: "endusers",
    method: "getOne",
    requests: enduserIds.length ? enduserIds : chatroom?.enduserIds ?? [],
    combine: (results) =>
      results.reduce<TellescopeModelFromName<"endusers">[]>((acc, curr) => {
        if (curr.data) {
          acc.push(curr.data);
        }
        return acc;
      }, []),
  });

  return (
    <DraftsAndSentItem
      key={draft.id}
      endusers={endusersQuery}
      {...rest}
      actions={[
        {
          key: "edit",
          icon: Pencil,
          hoverLabel: t("Edit Draft"),
          onClick: () => {
            if (draft.channel === "Chat" && chatroom) {
              return openThread({ type: draft.channel, message: chatroom, draftId: draft.id });
            } else if (draft.channel === "Chat") {
              return;
            }

            openThread({ type: draft.channel, message: draft, draftId: draft.id });
          },
        },
        {
          key: "delete",
          icon: Trash2,
          hoverLabel: t("Delete Draft"),
          onClick: () => {
            deleteMessage(draft.id, draft.channel);
          },
        },
      ]}
    />
  );
};
